import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import React from 'react'
import styled from 'styled-components'
import { ComplianceOverviewArea } from 'redux/compliance/complianceTypes'

const Card = styled.div`
  ${(props) => props.theme.baseCard}
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`

const StatBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 1rem 0.75rem;
  border-radius: 0.75rem;
  background-color: ${palette.grey.grey50};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const WorksiteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const WorksiteRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background-color: ${palette.grey.grey100};
`

const WorksiteInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
`

const StatusDot = styled.div<{ $compliant: boolean }>`
  width: 0.5rem;
  height: 0.5rem;
  margin-top: 0.4rem;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${(props) =>
    props.$compliant
      ? props.theme.palette.secondary.purple600
      : props.theme.palette.error.red600};
`

const WorksiteText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`

const StatusPill = styled.div<{ $variant: 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 999px;
  padding: 0.5rem 1rem;
  background-color: ${(props) =>
    props.$variant === 'success'
      ? props.theme.palette.secondary.purple100
      : props.theme.palette.error.red100};
  color: ${(props) =>
    props.$variant === 'success'
      ? props.theme.palette.secondary.purple700
      : props.theme.palette.error.red700};
  ${(props) => props.theme.typography.body2};
  font-weight: 600;
  white-space: nowrap;
`

const RequirementsList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const RequirementItem = styled.li`
  ${(props) => props.theme.typography.body2};
  color: ${palette.text.secondary};
`

interface WorksiteComplianceCardProps {
  area: ComplianceOverviewArea
}

const formatCount = (value: number) => value.toLocaleString('en-US')

const WorksiteComplianceCard = React.memo(
  (props: WorksiteComplianceCardProps) => {
    const { area } = props

    return (
      <Card>
        <Header>
          <Text variant='h4'>
            {area.city}, {area.state}
          </Text>
          <StatusPill $variant='success'>
            {area.compliantSites}/{area.totalSites} Compliant
          </StatusPill>
        </Header>

        <StatsRow>
          <StatBox>
            <Text variant='h3'>{formatCount(area.totalEmployees)}</Text>
            <Text variant='caption' color={palette.text.secondary}>
              Total Employees
            </Text>
          </StatBox>
          <StatBox>
            <Text variant='h3'>{formatCount(area.totalSites)}</Text>
            <Text variant='caption' color={palette.text.secondary}>
              Total Sites
            </Text>
          </StatBox>
        </StatsRow>

        <Section>
          <Text variant='action2'>Worksites in Zone:</Text>
          <WorksiteList>
            {area.worksites.map((worksite) => (
              <WorksiteRow key={worksite.id}>
                <WorksiteInfo>
                  <StatusDot $compliant={worksite.compliant} />
                  <WorksiteText>
                    <Text variant='action3'>
                      {worksite.formattedAddress || worksite.address}
                    </Text>
                    <Text variant='caption' color={palette.text.secondary}>
                      {formatCount(worksite.employeeCount)} employees
                    </Text>
                  </WorksiteText>
                </WorksiteInfo>
                <StatusPill $variant={worksite.compliant ? 'success' : 'danger'}>
                  {worksite.compliant ? 'Compliant' : 'Non-Compliant'}
                </StatusPill>
              </WorksiteRow>
            ))}
          </WorksiteList>
        </Section>

        {area.requirements.length > 0 && (
          <Section>
            <Text variant='action2'>Compliance Requirements:</Text>
            <RequirementsList>
              {area.requirements.map((requirement) => (
                <RequirementItem key={requirement.id}>
                  {requirement.text}
                </RequirementItem>
              ))}
            </RequirementsList>
          </Section>
        )}
      </Card>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  WorksiteComplianceCard.displayName = 'WorksiteComplianceCard'
}

export default WorksiteComplianceCard
